import React from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import ContactCTA from '../components/ContactCTA';

const OnyxLabs = () => {
  const services = [
    {
      title: 'AI and Machine Learning Strategy',
      description: 'Help organizations identify, design, and operationalize AI and ML initiatives.',
      icon: (
        <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      ),
    },
    {
      title: 'Data Engineering and Architecture',
      description: 'Design and implement scalable data pipelines, integration frameworks, and architectures.',
      icon: (
        <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
        </svg>
      ),
    },
    {
      title: 'Software Engineering and Product Development',
      description: 'Build modern, secure, and high-performing software systems.',
      icon: (
        <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
        </svg>
      ),
    },
    {
      title: 'Automation and Intelligent Workflows',
      description: 'Develop automated systems that streamline operations.',
      icon: (
        <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
    },
    {
      title: 'Technical Advisory and Innovation Consulting',
      description: 'Advise leaders on emerging technologies, system design, and implementation roadmaps.',
      icon: (
        <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
    },
  ];

  const approach = [
    { step: '1', title: 'Discover', description: 'Assess current systems and priorities to understand your challenges and opportunities.' },
    { step: '2', title: 'Design', description: 'Architect technical solutions tailored to your specific needs and goals.' },
    { step: '3', title: 'Build', description: 'Develop and deploy systems with technical excellence and best practices.' },
    { step: '4', title: 'Optimize', description: 'Continuously monitor and refine performance to ensure sustained success.' },
  ];

  const industries = [
    { name: 'Technology Companies', description: 'Developing or scaling AI-enabled products and digital platforms.' },
    { name: 'Healthcare Organizations', description: 'Seeking to modernize systems, improve data integration, and apply analytics for better performance.' },
    { name: 'Fintech Innovators', description: 'Leveraging automation, data intelligence, and AI to enhance customer experience and operational efficiency.' },
    { name: 'Mission-Driven Enterprises and Nonprofits', description: 'Using emerging technologies to advance social, economic, or community goals.' },
  ];

  return (
    <div>
      <Navigation />

      {/* Hero Section */}
      <section className="hero-section bg-gradient-to-br from-gray-900 via-onyx-900 to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl md:text-6xl text-white mb-9">
              Experts Accelerating Technology and Digital Innovation
            </h1>
            <p className="text-2xl italic text-white max-w-3xl mx-auto">
              Building intelligent solutions through data, design, and innovation.
            </p>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-shrink-0">
                <img src="/onyx-labs-logo.svg" alt="Onyx Labs Logo" className="w-48 h-48" />
              </div>
              <div className="hidden md:block w-px self-stretch bg-gray-300 flex-shrink-0"></div>
              <div className="flex-1">
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                  About Onyx Labs
                </h2>
                <p className="text-lg sm:text-xl text-gray-700 leading-relaxed">
                  Onyx Labs is the technology and innovation practice of Onyx Rose Advisors. We help organizations harness AI, data, and modern software development. Our team includes engineers, data scientists, and product strategists who bring deep technical expertise to every challenge.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Areas of Expertise
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
              We provide comprehensive technical services to accelerate innovation and digital transformation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center mb-6">
                  {service.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Approach Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our Approach
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
              We follow a structured, collaborative methodology from discovery to optimization.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {approach.map((item, index) => (
              <div
                key={index}
                className="text-center"
              >
                <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold">
                  {item.step}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {item.title}
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="py-20 bg-accent-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-white text-4xl font-bold">Target Industries</h2>
              <p className="mt-3 text-white/80 max-w-3xl">
                  We partner with forward-thinking organizations across diverse sectors. Browse the segments below.
              </p>

              <div className="relative mt-10">
                  {/* Left Arrow */}
                  <button
                      onClick={() => {
                          const container = document.getElementById('industries-carousel');
                          container.scrollBy({ left: -420, behavior: 'smooth' });
                      }}
                      className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white rounded-full p-3 shadow-lg transition-all"
                      aria-label="Scroll left"
                  >
                      <svg className="w-6 h-6 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                  </button>

                  {/* Right Arrow */}
                  <button
                      onClick={() => {
                          const container = document.getElementById('industries-carousel');
                          container.scrollBy({ left: 420, behavior: 'smooth' });
                      }}
                      className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white rounded-full p-3 shadow-lg transition-all"
                      aria-label="Scroll right"
                  >
                      <svg className="w-6 h-6 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                  </button>

                  <div id="industries-carousel" className="flex gap-5 overflow-x-auto snap-x snap-mandatory pb-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                      {industries.map((industry) => (
                          <div key={industry.name} className="snap-start shrink-0 w-[400px] rounded-2xl bg-white p-6 shadow-md flex gap-4">
                              <p className="text-lg font-semibold text-gray-900 w-2/5">{industry.name}</p>
                              <div className="w-px bg-gray-300 flex-shrink-0"></div>
                              <p className="text-md font-semibold text-gray-600 w-3/5">{industry.description}</p>
                          </div>
                      ))}
                  </div>
              </div>
          </div>
      </section>

      {/* Value Proposition Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-8">
              Why Choose Onyx Labs
            </h2>
            <div className="flex flex-col md:flex-row gap-12 mt-12 md:divide-x md:divide-gray-300">
              <div className="text-center md:px-8 flex-1">
                <h3 className="text-2xl font-bold mb-4">Strategic Consulting with Technical Execution</h3>
                <p className="text-gray-700 leading-relaxed">
                  We combine strategic consulting with hands-on technical implementation.
                </p>
              </div>
              <div className="text-center md:px-8 flex-1">
                <h3 className="text-2xl font-bold mb-4">Tangible Business Value</h3>
                <p className="text-gray-700 leading-relaxed">
                  We focus on creating measurable outcomes that drive real business impact.
                </p>
              </div>
              <div className="text-center md:px-8 flex-1">
                <h3 className="text-2xl font-bold mb-4">Technical Excellence and Outcomes</h3>
                <p className="text-gray-700 leading-relaxed">
                  We measure success by both technical excellence and business results.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ContactCTA
        title="Ready to Accelerate Your Innovation?"
        description="Contact us today to learn how Onyx Labs can help your organization harness AI, data, and modern technology to drive transformation."
        phoneNumber="+15716694008"
        phoneDisplay="(571) 669-4008"
      />

      <Footer />
    </div>
  );
};

export default OnyxLabs;
